import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Send, Loader2, CheckCircle } from "lucide-react";
import { z } from "zod";
import { trackContactFormSubmit } from "@/lib/analytics";

const contactSchema = z.object({
  name: z.string().trim().min(2, "Naam moet minimaal 2 tekens zijn").max(100, "Naam mag maximaal 100 tekens zijn"),
  email: z.string().trim().email("Ongeldig e-mailadres").max(255, "E-mail mag maximaal 255 tekens zijn"),
  subject: z.string().trim().min(3, "Onderwerp moet minimaal 3 tekens zijn").max(200, "Onderwerp mag maximaal 200 tekens zijn"),
  message: z.string().trim().min(10, "Bericht moet minimaal 10 tekens zijn").max(5000, "Bericht mag maximaal 5000 tekens zijn"),
});

type ContactFormData = z.infer<typeof contactSchema>;

// Rate limiting: max 3 submissions per 10 minutes
const RATE_LIMIT_KEY = "contact_form_submissions";
const RATE_LIMIT_MAX = 3;
const RATE_LIMIT_WINDOW_MS = 10 * 60 * 1000; // 10 minutes

function checkRateLimit(): boolean {
  try {
    const stored = localStorage.getItem(RATE_LIMIT_KEY);
    if (!stored) return true;
    
    const submissions: number[] = JSON.parse(stored);
    const now = Date.now();
    const recentSubmissions = submissions.filter(time => now - time < RATE_LIMIT_WINDOW_MS);
    
    return recentSubmissions.length < RATE_LIMIT_MAX;
  } catch {
    return true;
  }
}

function recordSubmission(): void {
  try {
    const stored = localStorage.getItem(RATE_LIMIT_KEY);
    const submissions: number[] = stored ? JSON.parse(stored) : [];
    const now = Date.now();
    const recentSubmissions = submissions.filter(time => now - time < RATE_LIMIT_WINDOW_MS);
    recentSubmissions.push(now);
    localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(recentSubmissions));
  } catch {
    // Ignore storage errors
  }
}

export const ContactForm = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof ContactFormData, string>>>({});
  const [formData, setFormData] = useState<ContactFormData>({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  
  // Honeypot field - should remain empty
  const [honeypot, setHoneypot] = useState("");
  
  // Time-based validation - form must be on screen for at least 3 seconds
  const formLoadTime = useRef(Date.now());
  const MIN_FORM_TIME_MS = 3000;

  const handleChange = (field: keyof ContactFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Anti-spam check 1: Honeypot
    if (honeypot) {
      console.log("Honeypot triggered");
      // Pretend success to confuse bots
      setIsSuccess(true);
      return;
    }
    
    // Anti-spam check 2: Time-based (form filled too quickly)
    const timeOnForm = Date.now() - formLoadTime.current;
    if (timeOnForm < MIN_FORM_TIME_MS) {
      console.log("Form submitted too quickly");
      toast({
        title: "Even geduld",
        description: "Neem even de tijd om het formulier in te vullen.",
        variant: "destructive",
      });
      return;
    }
    
    // Anti-spam check 3: Rate limiting
    if (!checkRateLimit()) {
      toast({
        title: "Te veel verzoeken",
        description: "Je hebt te veel berichten verstuurd. Probeer het later opnieuw.",
        variant: "destructive",
      });
      return;
    }
    
    // Validate form data
    const result = contactSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Partial<Record<keyof ContactFormData, string>> = {};
      result.error.errors.forEach(err => {
        const field = err.path[0] as keyof ContactFormData;
        if (!fieldErrors[field]) {
          fieldErrors[field] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }
    
    setIsSubmitting(true);
    setErrors({});
    
    try {
      const { data, error } = await supabase.functions.invoke("send-contact-email", {
        body: {
          name: result.data.name,
          email: result.data.email,
          subject: result.data.subject,
          message: result.data.message,
        },
      });
      
      if (error) throw error;
      
      recordSubmission();
      trackContactFormSubmit();
      setIsSuccess(true);
      setFormData({ name: "", email: "", subject: "", message: "" });
      
      toast({
        title: "Bericht verzonden!",
        description: "We nemen zo snel mogelijk contact met je op.",
      });
    } catch (error: any) {
      console.error("Error sending contact form:", error);
      toast({
        title: "Verzenden mislukt",
        description: "Er is iets misgegaan. Probeer het later opnieuw.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <Card className="p-8 bg-card text-center">
        <div className="flex flex-col items-center gap-4">
          <div className="p-4 bg-accent/20 rounded-full">
            <CheckCircle className="h-12 w-12 text-accent" />
          </div>
          <h3 className="text-xl font-semibold text-foreground">
            Bedankt voor je bericht!
          </h3>
          <p className="text-muted-foreground max-w-md">
            We hebben je bericht ontvangen en nemen zo snel mogelijk contact met je op.
          </p>
          <Button
            variant="outline"
            onClick={() => {
              setIsSuccess(false);
              formLoadTime.current = Date.now();
            }}
            className="mt-4"
          >
            Nog een bericht versturen
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-8 bg-card">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Honeypot field - hidden from users, visible to bots */}
        <div className="absolute -left-[9999px] opacity-0" aria-hidden="true">
          <Label htmlFor="website">Website</Label>
          <Input
            type="text"
            id="website"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
          />
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-foreground">
              Naam <span className="text-destructive">*</span>
            </Label>
            <Input
              id="name"
              type="text"
              placeholder="Je naam"
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              className={errors.name ? "border-destructive" : ""}
              disabled={isSubmitting}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-foreground">
              E-mailadres <span className="text-destructive">*</span>
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="je@email.nl"
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
              className={errors.email ? "border-destructive" : ""}
              disabled={isSubmitting}
            />
            {errors.email && (
              <p className="text-sm text-destructive">{errors.email}</p>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="subject" className="text-foreground">
            Onderwerp <span className="text-destructive">*</span>
          </Label>
          <Input
            id="subject"
            type="text"
            placeholder="Waar gaat je bericht over?"
            value={formData.subject}
            onChange={(e) => handleChange("subject", e.target.value)}
            className={errors.subject ? "border-destructive" : ""}
            disabled={isSubmitting}
          />
          {errors.subject && (
            <p className="text-sm text-destructive">{errors.subject}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="message" className="text-foreground">
            Bericht <span className="text-destructive">*</span>
          </Label>
          <Textarea
            id="message"
            placeholder="Typ hier je bericht..."
            rows={6}
            value={formData.message}
            onChange={(e) => handleChange("message", e.target.value)}
            className={errors.message ? "border-destructive" : ""}
            disabled={isSubmitting}
          />
          {errors.message && (
            <p className="text-sm text-destructive">{errors.message}</p>
          )}
          <p className="text-sm text-muted-foreground text-right">
            {formData.message.length} / 5000
          </p>
        </div>

        <Button
          type="submit"
          size="lg"
          className="w-full bg-accent hover:bg-accent/90 text-accent-foreground"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Verzenden...
            </>
          ) : (
            <>
              <Send className="mr-2 h-4 w-4" />
              Verstuur bericht
            </>
          )}
        </Button>

        <p className="text-xs text-muted-foreground text-center">
          Door dit formulier te versturen ga je akkoord met ons{" "}
          <a href="/privacy" className="text-accent hover:underline">
            privacybeleid
          </a>
          .
        </p>
      </form>
    </Card>
  );
};
